import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Theme preference control: Light / Dark / System, backed by next-themes.
//
// Two presentations share the same option set:
//   - <ThemeToggle />      a compact dropdown-menu (for the desktop partner navbar)
//   - <ThemeToggleRows />  full-width tappable rows that match the mobile ProfileSheet settings list
//
// Both guard against SSR/hydration mismatch with a `mounted` flag — next-themes only knows the resolved
// theme after mount, so we render a stable placeholder until then (avoids a flash of the wrong icon).

type ThemeChoice = 'light' | 'dark' | 'system';

const OPTIONS: { value: ThemeChoice; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

// Compact dropdown for the desktop navbar. Trigger shows the icon for the *active* choice.
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme();
  const mounted = useMounted();

  // Until mounted we don't know the persisted choice; default the icon to Monitor (system).
  const current = (mounted ? (theme as ThemeChoice) : 'system') ?? 'system';
  const ActiveIcon = OPTIONS.find((o) => o.value === current)?.icon ?? Monitor;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          aria-label="Change theme"
          className={cn(
            'bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary',
            className,
          )}
        >
          <ActiveIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40 bg-popover border-border z-50">
        <DropdownMenuRadioGroup value={current} onValueChange={(v) => setTheme(v)}>
          {OPTIONS.map(({ value, label, icon: Icon }) => (
            <DropdownMenuRadioItem
              key={value}
              value={value}
              className="text-popover-foreground hover:bg-accent hover:text-accent-foreground"
            >
              <Icon className="mr-2 h-4 w-4" />
              {label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Full-width segmented rows for the mobile settings sheet. Matches the ProfileSheet list aesthetic
// (rounded rows, accent hover, muted icons) and shows a check on the active choice.
export function ThemeToggleRows() {
  const { theme, setTheme } = useTheme();
  const mounted = useMounted();
  const current = (mounted ? (theme as ThemeChoice) : 'system') ?? 'system';

  return (
    <div className="space-y-1" role="radiogroup" aria-label="Theme">
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const active = mounted && current === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => setTheme(value)}
            className={cn(
              'flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent',
              active && 'bg-accent/60',
            )}
          >
            <span className="flex items-center gap-2">
              <Icon className="h-4 w-4" /> {label}
            </span>
            {active && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
}
